import { useEffect, useRef, useState } from 'react';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import type { Stop } from './api';
import { fullAddress, hasValidCoordinates, mapLink } from './customers';
import { loadGoogleMaps, MAPS_FAILURE_EVENT, useGoogleMapsSettings } from './googleMaps';
import { RouteMap } from './RouteMap';

export function GoogleRouteMap({
  stops,
  label,
  userId,
}: {
  stops: Stop[];
  label: string;
  userId: number | null;
}) {
  const maps = useGoogleMapsSettings(userId);
  const canvas = useRef<HTMLDivElement>(null);
  const [failure, setFailure] = useState(false);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [unresolved, setUnresolved] = useState(0);

  useEffect(() => {
    if (maps.state !== 'ready' || !canvas.current || failure) return;
    let cancelled = false;
    let map: google.maps.Map | undefined;
    let cluster: MarkerClusterer | undefined;
    let info: google.maps.InfoWindow | undefined;
    const markers: google.maps.marker.AdvancedMarkerElement[] = [];
    setLoading(true);
    setCount(0);
    setUnresolved(0);
    const fail = () => {
      if (!cancelled) setFailure(true);
    };
    window.addEventListener(MAPS_FAILURE_EVENT, fail);
    void loadGoogleMaps(maps.settings.browserKey)
      .then(async (mapsApi) => {
        const [{ Map, InfoWindow }, { AdvancedMarkerElement }] = await Promise.all([
          mapsApi.importLibrary('maps') as Promise<google.maps.MapsLibrary>,
          mapsApi.importLibrary('marker') as Promise<google.maps.MarkerLibrary>,
        ]);
        if (cancelled || !canvas.current) return;
        map = new Map(canvas.current, {
          center: { lat: 40.73, lng: -73.94 },
          zoom: 10,
          mapId: maps.settings.mapId,
          gestureHandling: 'cooperative',
          mapTypeControl: true,
          mapTypeControlOptions: { mapTypeIds: ['roadmap', 'satellite', 'hybrid'] },
          streetViewControl: true,
        });
        info = new InfoWindow();
        const bounds = new mapsApi.LatLngBounds();
        const fit = () => {
          if (markers.length === 1) {
            map!.setCenter(markers[0]!.position!);
            map!.setZoom(15);
          } else if (markers.length > 1) map!.fitBounds(bounds, 40);
          setCount(markers.length);
        };
        const add = (stop: Stop, position: google.maps.LatLngLiteral) => {
          if (cancelled) return;
          const marker = new AdvancedMarkerElement({ position, title: stop.name });
          marker.addListener('click', () => {
            const content = document.createElement('div');
            content.className = 'customer-map-info';
            const title = document.createElement('strong');
            title.textContent = stop.name;
            const address = document.createElement('p');
            address.textContent = fullAddress(stop);
            content.append(title, address);
            const url = mapLink(stop);
            if (url) {
              const link = document.createElement('a');
              link.textContent = 'Open in Google Maps';
              link.href = url;
              link.target = '_blank';
              link.rel = 'noopener noreferrer';
              content.append(link);
            }
            info!.setContent(content);
            info!.open({ map, anchor: marker });
          });
          markers.push(marker);
          bounds.extend(position);
        };
        for (const stop of stops) {
          if (hasValidCoordinates(stop)) add(stop, { lat: stop.latitude, lng: stop.longitude });
        }
        cluster = new MarkerClusterer({ map, markers });
        fit();
        setLoading(false);

        // New approved stops keep only a place ID. Resolve for this map session;
        // these coordinates never enter SQL, Blob, snapshots or browser storage.
        const pending = stops.filter((stop) => !hasValidCoordinates(stop) && stop.googlePlaceId);
        if (!pending.length) return;
        setUnresolved(pending.length);
        const { Place } = (await mapsApi.importLibrary('places')) as google.maps.PlacesLibrary;
        let next = 0;
        const worker = async () => {
          while (!cancelled && next < pending.length) {
            const stop = pending[next++]!;
            try {
              const place = new Place({ id: stop.googlePlaceId! });
              await place.fetchFields({ fields: ['location'] });
              if (!cancelled && place.location) {
                add(stop, place.location.toJSON());
                cluster!.addMarker(markers[markers.length - 1]!);
                fit();
              }
            } catch {
              // Keep the customer table usable when one location cannot resolve.
            } finally {
              if (!cancelled) setUnresolved((current) => Math.max(0, current - 1));
            }
          }
        };
        await Promise.all(Array.from({ length: Math.min(4, pending.length) }, worker));
      })
      .catch(fail);
    return () => {
      cancelled = true;
      window.removeEventListener(MAPS_FAILURE_EVENT, fail);
      info?.close();
      cluster?.clearMarkers();
      cluster?.setMap(null);
      for (const marker of markers) {
        marker.map = null;
        google.maps.event.clearInstanceListeners(marker);
      }
      if (map) google.maps.event.clearInstanceListeners(map);
    };
  }, [maps, stops, label, failure]);

  if (maps.state === 'unavailable' || failure) {
    return (
      <div className="route-map-fallback">
        <p className="map-availability">Street map is unavailable. Showing the route overview.</p>
        <RouteMap
          points={stops.flatMap((stop) =>
            hasValidCoordinates(stop)
              ? [{ latitude: stop.latitude, longitude: stop.longitude }]
              : [],
          )}
          total={stops.length}
          label={label}
          large
        />
      </div>
    );
  }
  return (
    <section
      className="route-map-large google-route-map"
      aria-label={`${label} customer locations`}
    >
      <div ref={canvas} className="google-route-map-canvas" />
      {(maps.state === 'loading' || loading) && (
        <div className="google-map-loading" role="status">
          Loading street map…
        </div>
      )}
      <div className="google-map-footer" role="status">
        <span>
          {count} of {stops.length} locations mapped{unresolved ? ' · Locating new stops…' : ''}
        </span>
        <span>Imported pins are estimated address locations.</span>
      </div>
    </section>
  );
}
